/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */
// Created/Modified by GitHub Copilot (Claude Opus 4.5) on 2026-09-03 by ido.kerner@quest.com
// Simplified legal notice license data import and cancel initialization timer during teardown.

import { Subject } from 'rxjs';
import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DataTableOptions, TableState } from '@foglight/angular-common';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import jsonData from './third-party-licenses.json';

export class LegalNoticeItem {
  constructor(
    public componentName: string,
    public componentVersion: string,
    public license: string,
    public licenseUrl: string,
  ) {}
}

@Component({
  selector: 'fc-legal-notice',
  templateUrl: './legal-notice.component.html',
  styleUrls: ['./legal-notice.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegalNoticeComponent implements OnInit, OnDestroy {
  private componentDestroy$ = new Subject<void>();
  private initializationTimer?: ReturnType<typeof setTimeout>;
  private allItems: LegalNoticeItem[] = jsonData;
  searchFormControl = new UntypedFormControl('');
  dataSource!: MatTableDataSource<LegalNoticeItem>;
  tableState: TableState = TableState.loadingSkeleton;

  tableOptions: DataTableOptions = {
    paginator: { paginator: false },
    isRowSelectable: false,
    sortDirection: 'asc',
    sortActive: 'componentName',
    exportOptions: { enabled: false },
    infiniteScroll: true
  };

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Delay so fac-data-table resolves its ContentChildren (columns) before receiving data
    this.initializationTimer = setTimeout(() => {
      this.dataSource = new MatTableDataSource<LegalNoticeItem>(this.allItems);
      this.tableState = TableState.showData;
      this.cdr.detectChanges();
    }, 50);

    this.searchFormControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.componentDestroy$))
      .subscribe((searchKey: string) => {
        this.filterData(searchKey);
      });
  }

  private filterData(searchKey: string): void {
    if (!searchKey) {
      this.updateDataSource(this.allItems);
      return;
    }
    const lower = searchKey.toLowerCase();
    const filtered = this.allItems.filter(
      (item) =>
        item.componentName?.toLowerCase().includes(lower) ||
        item.componentVersion?.toLowerCase().includes(lower) ||
        item.license?.toLowerCase().includes(lower),
    );
    this.updateDataSource(filtered);
  }

  private updateDataSource(items: LegalNoticeItem[]): void {
    this.dataSource = new MatTableDataSource<LegalNoticeItem>(items);
    this.tableState =
      items.length > 0 ? TableState.showData : TableState.nothingFound;
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.initializationTimer) {
      clearTimeout(this.initializationTimer);
    }
    this.componentDestroy$.next();
    this.componentDestroy$.complete();
  }
}
